// ═══════════════════════════════════════════════════════════════════════════
//  BOT MEMORY — Rudra bots ka DIMAG (short memory + long memory)
//
//  SHORT memory  = recent events per bot (last trades, notes) — rolling window.
//  LONG memory   = symbol-wise lifetime record per bot (trades/WR/PnL/streaks),
//                  built from the canonical /api/trades/ledger — never fabricated.
//  DEPTH memory  = latest 20-LEVEL order book snapshot per symbol (from the
//                  Live Trading Market Depth WS) — buy-low/sell-high bias input.
//
//  Self-analysis = deterministic rules over LONG+SHORT+DEPTH memory that emit
//  Pine tuning suggestions per bot DNA (Vega=options scalp, Shool=futures SMC,
//  Sthambh=cash BUY-only). Har SUNDAY ek auto smart-upgrade report generate
//  hota hai (once per week) jo Mamta Chat me post hota hai.
// ═══════════════════════════════════════════════════════════════════════════

import { getAllGodVerdicts } from '../components/SuperChart/godChakra';
import { getChartRead, chartVisionBrief, TRADING_RULES_BRIEF } from '../components/SuperChart/chartPerception';

export interface MemDepthLevel { price: number; qty: number; orders?: number; }
export interface DepthSnap {
  symbol: string; ts: number; ltp?: number; spread?: number;
  bids: MemDepthLevel[]; asks: MemDepthLevel[];   // max 20 each — full Dhan book
  bidQty: number; askQty: number;
  imbalance: number;   // bidQty / (bidQty+askQty) — >0.6 = bid-heavy (buyers), <0.4 = ask-heavy
}

export interface SymbolMemory {
  symbol: string;
  trades: number; wins: number; losses: number;
  netPnl: number; grossProfit: number; grossLoss: number;
  bestTrade: number; worstTrade: number;
  lastStatus: string; lastPnl: number; lastTs: string;   // dateIso + time
  curLossStreak: number; maxLossStreak: number;
}

export interface MemEvent {
  ts: number; kind: 'TRADE' | 'NOTE';
  symbol: string; text: string; pnl?: number; status?: string;
}

export interface BotAnalysis {
  botId: string;
  headline: string;
  symbolRows: SymbolMemory[];        // sorted — best netPnl first
  suggestions: string[];             // Hinglish, actionable, DNA-safe
  shortCount: number; longCount: number; depthCount: number;
}

// ── storage keys ────────────────────────────────────────────────────────────
const K_SHORT  = 'jc_bot_mem_short_v1';    // Record<botId, MemEvent[]>
const K_LONG   = 'jc_bot_mem_long_v1';     // Record<botId, Record<symbol, SymbolMemory>>
const K_DEPTH  = 'jc_bot_mem_depth_v1';    // Record<symbol, DepthSnap>
const K_SEEN   = 'jc_bot_mem_seen_v1';     // string[] — ledger trade ids already absorbed
const K_SUNDAY = 'jc_bot_mem_sunday_v1';   // 'YYYY-WW' — last weekly report week

const SHORT_MAX = 80;      // events per bot
const DEPTH_MAX = 40;      // symbols with a stored book
const SEEN_MAX  = 4000;    // absorbed trade-id ring

const _get = <T,>(k: string, fb: T): T => {
  try { const r = localStorage.getItem(k); return r ? JSON.parse(r) as T : fb; } catch { return fb; }
};
const _set = (k: string, v: unknown) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* quota */ } };

// Ledger botIds are deployment ids (mai_veg / bot_...) — cards use short ids.
export const normBotId = (id: string): string => String(id || '').replace(/^mai_/, '').toLowerCase();

// ── DEPTH memory (20-level book, from Live Trading WS) ─────────────────────
export function recordDepthSnap(symbol: string, bids: MemDepthLevel[], asks: MemDepthLevel[], ltp?: number, spread?: number): void {
  const s = String(symbol || '').toUpperCase().trim();
  if (!s || (!bids?.length && !asks?.length)) return;
  const all = _get<Record<string, DepthSnap>>(K_DEPTH, {});
  const bidQty = (bids || []).reduce((a, b) => a + (Number(b.qty) || 0), 0);
  const askQty = (asks || []).reduce((a, b) => a + (Number(b.qty) || 0), 0);
  all[s] = {
    symbol: s, ts: Date.now(), ltp, spread,
    bids: (bids || []).slice(0, 20), asks: (asks || []).slice(0, 20),
    bidQty, askQty,
    imbalance: (bidQty + askQty) > 0 ? bidQty / (bidQty + askQty) : 0.5,
  };
  // cap — evict the OLDEST snapshots beyond DEPTH_MAX
  const keys = Object.keys(all);
  if (keys.length > DEPTH_MAX) {
    keys.sort((a, b) => (all[a].ts || 0) - (all[b].ts || 0))
      .slice(0, keys.length - DEPTH_MAX).forEach(k => delete all[k]);
  }
  _set(K_DEPTH, all);
}

export const getDepthSnap = (symbol: string): DepthSnap | null =>
  _get<Record<string, DepthSnap>>(K_DEPTH, {})[String(symbol || '').toUpperCase().trim()] || null;

export const getDepthCount = (): number => Object.keys(_get<Record<string, DepthSnap>>(K_DEPTH, {})).length;

// ── LONG + SHORT memory sync from the canonical ledger ─────────────────────
// Dedupe on trade id so repeated polls never double-count. Never fabricates —
// only absorbs closed rows (WIN/LOSS/BREAKEVEN) that the ledger reports.
export function syncMemoryFromLedger(rows: Array<Record<string, any>>): number {
  if (!Array.isArray(rows) || !rows.length) return 0;
  const seen = new Set<string>(_get<string[]>(K_SEEN, []));
  const long  = _get<Record<string, Record<string, SymbolMemory>>>(K_LONG, {});
  const short = _get<Record<string, MemEvent[]>>(K_SHORT, {});
  let absorbed = 0;

  for (const t of rows) {
    const status = String(t.status || '');
    if (!['WIN', 'LOSS', 'BREAKEVEN'].includes(status)) continue;
    const tid = String(t.id ?? t.tradeId ?? `${t.botId}|${t.dateIso}|${t.time}|${t.symbol}|${t.netPnl}`);
    if (seen.has(tid)) continue;
    seen.add(tid); absorbed++;

    const botId = normBotId(String(t.botId || 'unknown'));
    const sym = String(t.optionSymbol || t.symbol || '?').toUpperCase();
    const pnl = Number(t.netPnl ?? t.pnl) || 0;
    const ts = `${t.dateIso || ''} ${t.time || ''}`.trim();

    // LONG — symbol-wise aggregate
    const botLong = long[botId] || (long[botId] = {});
    const m = botLong[sym] || (botLong[sym] = {
      symbol: sym, trades: 0, wins: 0, losses: 0, netPnl: 0, grossProfit: 0, grossLoss: 0,
      bestTrade: 0, worstTrade: 0, lastStatus: '', lastPnl: 0, lastTs: '', curLossStreak: 0, maxLossStreak: 0,
    });
    m.trades++; m.netPnl += pnl;
    if (status === 'WIN')  { m.wins++;  m.grossProfit += pnl; m.curLossStreak = 0; }
    if (status === 'LOSS') { m.losses++; m.grossLoss += Math.abs(pnl); m.curLossStreak++; m.maxLossStreak = Math.max(m.maxLossStreak, m.curLossStreak); }
    if (status === 'BREAKEVEN') m.curLossStreak = 0;
    m.bestTrade = Math.max(m.bestTrade, pnl); m.worstTrade = Math.min(m.worstTrade, pnl);
    m.lastStatus = status; m.lastPnl = pnl; m.lastTs = ts;

    // SHORT — rolling recent-events window
    const ev = short[botId] || (short[botId] = []);
    ev.push({ ts: Date.now(), kind: 'TRADE', symbol: sym, status, pnl,
      text: `${status} ${sym} ${pnl >= 0 ? '+' : ''}₹${Math.round(pnl)}` });
    if (ev.length > SHORT_MAX) ev.splice(0, ev.length - SHORT_MAX);
  }

  if (absorbed) {
    const seenArr = Array.from(seen);
    _set(K_SEEN, seenArr.slice(Math.max(0, seenArr.length - SEEN_MAX)));
    _set(K_LONG, long); _set(K_SHORT, short);
  }
  return absorbed;
}

export const getLongMemory = (botId: string): SymbolMemory[] =>
  Object.values(_get<Record<string, Record<string, SymbolMemory>>>(K_LONG, {})[normBotId(botId)] || {})
    .sort((a, b) => b.netPnl - a.netPnl);

export const getShortMemory = (botId: string): MemEvent[] =>
  _get<Record<string, MemEvent[]>>(K_SHORT, {})[normBotId(botId)] || [];

// ── SELF-ANALYSIS — deterministic, DNA-safe suggestions ────────────────────
const inr = (n: number) => `₹${Math.abs(Math.round(n)).toLocaleString('en-IN')}`;
const wr = (m: SymbolMemory) => m.trades ? Math.round((m.wins / m.trades) * 100) : 0;

export function analyzeBot(botId: string, botName?: string): BotAnalysis {
  const id = normBotId(botId);
  const rows = getLongMemory(id);
  const short = getShortMemory(id);
  const sugg: string[] = [];
  const name = botName || id.toUpperCase();

  const total = rows.reduce((a, r) => ({ t: a.t + r.trades, w: a.w + r.wins, p: a.p + r.netPnl }), { t: 0, w: 0, p: 0 });
  const totWr = total.t ? Math.round((total.w / total.t) * 100) : 0;

  // 1) Weak symbols — low WR with enough sample → OFF ya confluence tighten
  rows.filter(r => r.trades >= 5 && wr(r) < 40).forEach(r => {
    sugg.push(`⚠ ${r.symbol}: sirf ${wr(r)}% WR (${r.trades} trades, ${r.netPnl < 0 ? '−' : '+'}${inr(r.netPnl)}) — is symbol ko OFF karo ya SMC confluence 2-of-3 se 3-of-3 karo (Order Block + FVG + Liquidity Sweep TEENO align hone par hi entry).`);
  });

  // 2) Risk skew — avg loss > avg win → SL tighten (buy low, sell high discipline)
  const gp = rows.reduce((a, r) => a + r.grossProfit, 0), gl = rows.reduce((a, r) => a + r.grossLoss, 0);
  const wins = rows.reduce((a, r) => a + r.wins, 0), losses = rows.reduce((a, r) => a + r.losses, 0);
  const avgW = wins ? gp / wins : 0, avgL = losses ? gl / losses : 0;
  if (losses >= 3 && avgL > avgW * 1.4) {
    sugg.push(id === 'veg'
      ? `📉 Avg loss (${inr(avgL)}) avg win (${inr(avgW)}) se bada hai — Pine me sl_pts kam karo (5→4) ya tgt_pts badhao (10→12): LOW price buy, HIGH price sell ka ratio sudhrega.`
      : `📉 Avg loss (${inr(avgL)}) avg win (${inr(avgW)}) se bada hai — Pine me sl_atr tight karo (2.0→1.6) taaki losers jaldi cut hon aur winners chalte rahen.`);
  }

  // 3) Loss streak per symbol → cooldown
  rows.filter(r => r.curLossStreak >= 3).forEach(r => {
    sugg.push(`🧊 ${r.symbol}: ${r.curLossStreak} lagatar LOSS chal rahe hain — cooldown lagao (agla signal skip) aur support/resistance zone dobara verify karo; trend 50 EMA ke against entry mat lo.`);
  });

  // 4) Best symbol — double down
  const best = rows.find(r => r.trades >= 5 && wr(r) >= 60 && r.netPnl > 0);
  if (best) sugg.push(`💎 ${best.symbol}: ${wr(best)}% WR aur +${inr(best.netPnl)} — yeh ${name} ka best symbol hai; iske lots par focus rakho, kamzor symbols ka risk yahan shift karo.`);

  // 5) Depth-bias check on symbols the bot recently traded (buy-low/sell-high gate)
  const recentSyms = Array.from(new Set(short.slice(-10).map(e => e.symbol)));
  for (const s of recentSyms.slice(0, 4)) {
    const d = getDepthSnap(s) || getDepthSnap(s.split(' ')[0]);
    if (!d) continue;
    if (d.imbalance >= 0.62) sugg.push(`📊 ${d.symbol} ka 20-level book BID-heavy hai (${Math.round(d.imbalance * 100)}% buyers) — buy-side entries ko support milta hai; sell/PE entries par extra confirmation lo.`);
    else if (d.imbalance <= 0.38) sugg.push(`📊 ${d.symbol} ka 20-level book ASK-heavy hai (${Math.round((1 - d.imbalance) * 100)}% sellers) — resistance upar strong hai; buy entries par extra confirmation lo${id === 'sthambh' ? ' (Sthambh BUY-only: entry ruk kar lo, short kabhi nahi)' : ''}.`);
  }

  // 5b) CHART VISION — live SuperChart read on recently traded symbols. The
  // bot reacts to what the chart is doing NOW (phase/structure/levels), not
  // only to its past record.
  for (const s of recentSyms.slice(0, 4)) {
    const cr = getChartRead(s) || getChartRead(s.split(' ')[0]);
    if (!cr || Date.now() - cr.at > 120000) continue;
    if (id === 'veg' && (cr.phase === 'RANGING' || cr.phase === 'VOLATILE_CHOP')) {
      sugg.push(`👁 ${cr.symbol} ${cr.tf} chart abhi ${cr.phase === 'RANGING' ? 'RANGE me atka' : 'VOLATILE CHOP me'} hai (${cr.summary}) — Vega scalp entries chop me phasti hain; GOD CHAKRA ALIGNED hone tak signal skip karo.`);
    }
    if (id === 'sthambh' && cr.structure === 'LH_LL') {
      sugg.push(`👁 ${cr.symbol} chart LOWER-HIGH LOWER-LOW structure bana raha hai — Sthambh BUY-only hai, naya entry structure HH-HL me flip hone ke BAAD hi lo (falling knife mat pakdo).`);
    }
    if (cr.levelNote === 'AT_RESISTANCE') {
      sugg.push(`👁 ${cr.symbol} price RESISTANCE zone par khada hai — fresh BUY yahan mat lo; breakout+retest ya support tak pullback ka wait karo.`);
    } else if (cr.levelNote === 'AT_SUPPORT' && cr.god?.action === 'BUY') {
      sugg.push(`👁 ${cr.symbol} SUPPORT zone par hai aur GOD CHAKRA ${cr.god.confluence}% BUY bol raha hai — buy-low ka textbook setup; SL support ke thoda niche rakho.`);
    }
    // Rule 2: 60-80% retrace = best price. Chase mat karo — zone ka wait.
    const ep = cr.entryPlan;
    if (ep) {
      const z = (v: number) => v.toFixed(v >= 1000 ? 0 : 1);
      sugg.push(ep.status === 'IN_ZONE'
        ? `◎ ${cr.symbol} ${cr.tf}: price ABHI 60-80% retrace zone ke andar hai (${z(ep.zoneLo)}–${z(ep.zoneHi)}) — best-price ${ep.side} window; SL ${z(ep.sl)}, exit T1 ${z(ep.t1)} par partial + T2 ${z(ep.t2)} par full book karo (giveback se pehle).`
        : `◎ ${cr.symbol} ${cr.tf}: ${ep.side} ka best price ${z(ep.zoneLo)}–${z(ep.zoneHi)} retrace zone me hai — running candle chase mat karo, pullback ka wait; SL ${z(ep.sl)}, targets ${z(ep.t1)}/${z(ep.t2)}.`);
    }
  }

  // 6) DNA-specific structural upgrade
  if (id === 'veg' && total.t >= 10) {
    sugg.push(`🧬 Vega Pine me abhi sirf EMA cross + IV filter hai — Shool jaisa SMC filter add karo (Order Block retest + FVG + Liquidity Sweep, >=2 of 3) taaki scalp entries LOW (demand zone) par lagen aur exits HIGH (supply zone) par.`);
  }
  if (id === 'shool') {
    const dud = rows.filter(r => r.trades >= 8 && wr(r) >= 40 && wr(r) < 50);
    if (dud.length) sugg.push(`🧬 Shool: ${dud.map(r => r.symbol).join(', ')} par WR 40-50% atka hai — smc_bars window 6→4 karo (fresh confluence hi lo) aur sweep_len 20→30 se bade liquidity pools pakdo.`);
  }
  if (id === 'sthambh' && rows.some(r => r.curLossStreak >= 2)) {
    sugg.push(`🧬 Sthambh (BUY-only CNC): entry se pehle 1D support zone confirm karo — 4H/1D EMA50>200 gate ke saath weekly support retest add karna drawdown kam karega. SELL signal sirf EXIT hai, short kabhi nahi.`);
  }

  if (!sugg.length) sugg.push(total.t
    ? `✅ ${name} ki memory healthy hai — ${total.t} trades, ${totWr}% WR, ${total.p >= 0 ? '+' : '−'}${inr(total.p)}. Abhi koi urgent Pine change nahi; SMC checklist (OB/FVG/Sweep/S-R) engine follow kar raha hai.`
    : `🕐 ${name} ki memory abhi khali hai — trades aate hi symbol-wise record yahan banega aur self-analysis suggestions milenge.`);

  return {
    botId: id,
    headline: total.t
      ? `${total.t} trades · ${totWr}% WR · ${total.p >= 0 ? '+' : '−'}${inr(total.p)} net · ${rows.length} symbols yaad hain`
      : 'Memory building… (pehla trade aane par record shuru)',
    symbolRows: rows,
    suggestions: sugg.slice(0, 6),
    shortCount: short.length, longCount: rows.length, depthCount: getDepthCount(),
  };
}

// ── Compact memory brief for AI prompts (Mamta Chat / Pine co-pilot) ────────
export function memoryBrief(botId?: string): string {
  const ids = botId ? [normBotId(botId)] : ['veg', 'shool', 'sthambh'];
  const parts: string[] = [];
  for (const id of ids) {
    const rows = getLongMemory(id);
    if (!rows.length) continue;
    const top = rows.slice(0, 3).map(r => `${r.symbol} ${r.trades}t/${wr(r)}%WR/${r.netPnl >= 0 ? '+' : ''}${Math.round(r.netPnl)}`).join(', ');
    parts.push(`${id.toUpperCase()} memory: ${top}${rows.length > 3 ? ` (+${rows.length - 3} more)` : ''}`);
  }
  const bits = [
    // The user's golden rules ride in EVERY prompt — permanent training.
    TRADING_RULES_BRIEF,
    parts.length ? `[BOT LONG MEMORY (real ledger record) — ${parts.join(' | ')}]` : '',
    godChakraBrief(),
    // Live chart perception — the AI reads the SuperChart like a trader
    // (phase/structure/momentum/volume/patterns), not just past trades.
    chartVisionBrief(),
  ].filter(Boolean);
  return bits.join(' ');
}

// ── GOD CHAKRA brief — live multi-TF SMC read per charted symbol ────────────
// The SuperChart publishes a per-symbol verdict (chart-TF signal + higher-TF
// direction filter, all computed by the backend SMC engine). Injecting it
// here lets Mamta AI / Pine co-pilot reason about CURRENT price behaviour,
// not just past trades. Fresh = published within the last 2 minutes.
export function godChakraBrief(): string {
  try {
    const snaps = getAllGodVerdicts().filter(s => Date.now() - s.at < 120000);
    if (!snaps.length) return '';
    const rows = snaps.slice(0, 6).map(s => {
      const htf = s.htf.map(h => `${h.tf}${h.dir === 'BULL' ? '↑' : h.dir === 'BEAR' ? '↓' : '·'}`).join(' ');
      return `${s.symbol}@${s.chartTf}: ${s.action} ${s.confluence}%${s.aligned ? ' ALIGNED' : ''} (${htf})`;
    });
    return `[GOD CHAKRA (live SMC multi-TF view) — ${rows.join(' | ')}]`;
  } catch { return ''; }
}

// ── SUNDAY smart upgrade — once per ISO week, on Sundays only ───────────────
const _weekKey = (d: Date): string => {
  // ISO week number — Sunday runs use the week that just ENDED
  const t = new Date(Date.UTC(d.getFullYear(), d.getMonth(), d.getDate()));
  const day = t.getUTCDay() || 7;
  t.setUTCDate(t.getUTCDate() + 4 - day);
  const y0 = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const wk = Math.ceil((((t.getTime() - y0.getTime()) / 86400000) + 1) / 7);
  return `${t.getUTCFullYear()}-W${wk}`;
};

export function sundaySmartCheck(bots: Array<{ id: string; name: string }>): { weekKey: string; text: string } | null {
  const now = new Date();
  if (now.getDay() !== 0) return null;                       // Sunday only
  const wk = _weekKey(now);
  if (_get<string>(K_SUNDAY, '') === wk) return null;        // once per week
  _set(K_SUNDAY, wk);

  const secs = bots.map(b => {
    const a = analyzeBot(b.id, b.name);
    return `**${b.name}** — ${a.headline}\n${a.suggestions.map(s => `• ${s}`).join('\n')}`;
  });
  return {
    weekKey: wk,
    text: `🧠 **SUNDAY SELF-ANALYSIS (${wk})** — bots ne apni short+long memory padh kar Pine upgrade suggestions nikale:\n\n${secs.join('\n\n')}\n\n💡 Pine Editor me jaakar "Improve" dabao — yeh memory report AI ke paas context me jayegi aur code DNA-safe upgrade hoga.`,
  };
}
